import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import SearchBar from "@/components/feature/SearchBar";
import ContentListItem from "@/components/feature/ContentListItem";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import FilterSidebar, { type FilterSection } from "@/components/feature/FilterSidebar";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { contentItems, newsCategories } from "@/mocks/content";
import { searchContent, type SortKey } from "@/utils/search";

const PAGE_SIZE = 5;

export default function News() {
  const [params, setParams] = useSearchParams();
  const [showFilter, setShowFilter] = useState(false);

  const cat = params.get("cat") || "全部";
  const sort = (params.get("sort") as SortKey) || "time";
  const page = Number(params.get("page") || "1") || 1;

  const result = searchContent({ type: "news", category: cat, sort, page, pageSize: PAGE_SIZE });

  useDocumentMeta({
    title: "新闻分类 - 百度一下",
    description: "百度一下新闻频道，提供最新国内外新闻信息浏览，涵盖国内、国际、财经、体育、科技等分类内容。",
    keywords: "百度一下新闻,最新新闻,国内新闻,国际新闻,财经新闻,体育新闻",
  });

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (!value || value === "全部") next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next);
  };

  const goPage = (p: number) => {
    const next = new URLSearchParams(params);
    if (p <= 1) next.delete("page");
    else next.set("page", String(p));
    setParams(next);
  };

  const countCat = (c: string) =>
    contentItems.filter((i) => i.type === "news" && (c === "全部" || i.category === c)).length;

  const sections: FilterSection[] = [
    {
      title: "新闻分类",
      value: cat,
      options: newsCategories.map((c) => ({ label: c, value: c, count: countCat(c) })),
      onChange: (v) => updateParam("cat", v),
    },
    {
      title: "排序",
      value: sort,
      options: [
        { label: "按时间（倒序）", value: "time" },
        { label: "按热度", value: "relevance" },
      ],
      onChange: (v) => updateParam("sort", v),
    },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="w-full px-4 py-8 md:px-6 md:py-10">
            <div className="mx-auto max-w-6xl">
              <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                新闻
              </h1>
              <p className="mt-2 text-sm text-foreground-600">
                汇集国内外最新资讯，按分类浏览，内容按时间倒序排列。
              </p>
              <div className="mt-5">
                <SearchBar
                  placeholder="在新闻频道内搜索关键词"
                  onSearch={(kw) => setParams({ q: kw, type: "news" })}
                />
              </div>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr]">
            <aside>
              <button
                type="button"
                onClick={() => setShowFilter((v) => !v)}
                className="mb-3 flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-700 lg:hidden"
              >
                <i className="ri-filter-3-line" />
                筛选分类
                <i className={`ri-arrow-down-s-line transition-transform ${showFilter ? "rotate-180" : ""}`} />
              </button>
              <div className="hidden lg:block">
                <FilterSidebar sections={sections} />
              </div>
              {showFilter && (
                <div className="lg:hidden">
                  <FilterSidebar sections={sections} />
                </div>
              )}

              <div className="mt-4 hidden rounded-lg border border-background-200 bg-background-100 p-4 lg:block">
                <p className="text-xs leading-relaxed text-foreground-600">
                  演示范围，新闻内容为示例数据，排序与筛选基于前端模拟。
                </p>
              </div>
            </aside>

            <div className="min-w-0">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  {cat === "全部" ? "全部新闻" : `${cat}新闻`}
                </h2>
                <span className="text-sm text-foreground-500">共 {result.total} 条</span>
              </div>

              {result.items.length > 0 ? (
                <>
                  <div className="flex flex-col gap-4">
                    {result.items.map((item) => (
                      <ContentListItem key={item.id} item={item} />
                    ))}
                  </div>
                  <Pagination current={result.page} totalPages={result.totalPages} onChange={goPage} />
                </>
              ) : (
                <EmptyState
                  title="该分类下暂无内容"
                  description="换一个新闻分类，或返回首页查看推荐内容。"
                  actions={
                    <>
                      <button
                        type="button"
                        onClick={() => setParams({})}
                        className="rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
                      >
                        查看全部新闻
                      </button>
                      <Link
                        to="/"
                        className="rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 whitespace-nowrap transition-colors hover:border-primary-400 hover:text-primary-600"
                      >
                        返回首页
                      </Link>
                    </>
                  }
                />
              )}
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}